import i18n from '@app/i18n';
import { formatBalanceUi } from '@utils/balance-helpers';
import { currencySymbols } from '@utils/static';

// Получаем локаль для форматирования дат на основе текущего языка
function getLocale() {
  const lang = i18n.language || 'en';
  return lang === 'ru' ? 'ru-RU' : 'en-US';
}

const chartBase = {
  fontFamily: 'Golos Text',
  toolbar: { show: false },
  zoom: { enabled: false },
  animations: { enabled: false },
  parentHeightOffset: 0,
};

const gridBase = {
  borderColor: '#c5c5c5',
  strokeDashArray: 4,
};

const axisLabelStyle = {
  fontSize: '12px',
  fontWeight: 400,
};

export function getColorsByPercentage(value, maxValue) {
  const max = Number(maxValue) || 0;
  const percent = max > 0 ? ((Number(value) || 0) / max) * 100 : 0;

  let activeColor = 'var(--brand-primary-accent)';
  if (percent > 80) {
    activeColor = 'var(--brand-warning)';
  } else if (percent > 50) {
    activeColor = 'var(--brand-attention)';
  }

  return [activeColor, 'var(--basic-sep)'];
}

//------- donut chart
export const donutChartOptions = {
  options: {
    chart: {
      type: 'donut',
    },
    dataLabels: {
      enabled: false,
    },
    states: {
      hover: {
        filter: {
          type: 'none',
        },
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '48%',
        },
        expandOnClick: false,
      },
    },
    legend: {
      show: false,
    },
    stroke: {
      show: false,
    },
    tooltip: {
      enabled: false,
    },
  },
};
//-------

//------- spent chart
const spentChartBaseOptions = {
  colors: ['var(--brand-attention)', 'var(--brand-color-chart)'],
  chart: {
    type: 'bar',
    stacked: true,
    ...chartBase,
  },
  stroke: {
    width: 1,
    colors: ['#fff'],
  },
  grid: gridBase,
  states: {
    hover: { filter: { type: 'none' } },
    active: { filter: { type: 'none' } },
  },
  plotOptions: {
    bar: {
      horizontal: false,
      distributed: false,
      columnWidth: '38%',
      borderRadius: 3,
      dataLabels: { total: { enabled: false } },
    },
  },
  dataLabels: {
    enabled: false,
    style: {
      fontSize: '8px',
      fontWeight: 400,
      colors: ['var(--text-secondary-color)'],
    },
  },
  xaxis: {
    categories: [],
    labels: {
      style: { ...axisLabelStyle, cssClass: 'apexcharts-xaxis-label' },
    },
  },
  yaxis: {
    labels: {
      show: true,
    },
    style: { ...axisLabelStyle, cssClass: 'apexcharts-yaxis-label' },
  },
  legend: { show: false },
  fill: { opacity: 1 },
  tooltip: {
    shared: true,
    intersect: false,
    followCursor: true,
  },
};

function formatPeriodLabel(period, key, now = new Date()) {
  if (!key) return '';
  const locale = getLocale();
  const currentYear = now.getFullYear();

  switch (period) {
    case 'today':
      return key;

    case 'week': {
      const date = new Date(key);
      if (Number.isNaN(date.getTime())) return key;
      return date.toLocaleString(locale, { weekday: 'short' });
    }

    case 'month': {
      const date = new Date(key);
      if (Number.isNaN(date.getTime())) return key;
      return date.toLocaleString(locale, {
        month: 'short',
        day: 'numeric',
      });
    }

    case 'year': {
      const [mm, yyyy] = key.split('-').map(Number);
      if (!mm || !yyyy) return key;
      const date = new Date(yyyy, mm - 1, 1);
      if (yyyy === currentYear) {
        return date.toLocaleString(locale, { month: 'short' });
      }
      return date.toLocaleString(locale, {
        month: 'short',
        year: '2-digit',
      });
    }

    default:
      return key;
  }
}

export function mapPeriodDataToSeries(periodBlock, period) {
  const data = periodBlock?.data;
  if (!data) {
    return {
      categories: [],
      series: [
        { name: 'Hold', data: [] },
        { name: 'Charged', data: [] },
      ],
    };
  }

  const keys = Object.keys(data);
  return {
    categories: keys.map((k) => formatPeriodLabel(period, k)),
    series: [
      {
        name: 'Hold',
        data: keys.map((k) => Number(data[k]?.hold) || 0),
      },
      {
        name: 'Charged',
        data: keys.map((k) => {
          const hold = Number(data[k]?.hold) || 0;
          const spent = Number(data[k]?.spent) || 0;
          return spent - hold;
        }),
      },
      // TODO: вернуть declined
      // {
      //   name: 'Declined',
      //   data: keys.map((k) => Number(data[k]?.declined) || 0),
      // },
    ],
  };
}

export function getSpentChartOptions(
  period,
  categories = [],
  currency = 'USD',
) {
  const symbol = currencySymbols[currency] || '';

  return {
    ...spentChartBaseOptions,
    xaxis: {
      ...spentChartBaseOptions.xaxis,
      categories,
    },
    yaxis: {
      ...spentChartBaseOptions.yaxis,
      labels: {
        ...spentChartBaseOptions.yaxis.labels,
        formatter: (val) => (val ? `${val}${symbol}` : ''),
      },
    },
    dataLabels: {
      ...spentChartBaseOptions.dataLabels,
      formatter: (val) => `${val}${symbol}`,
    },

    tooltip: {
      ...spentChartBaseOptions.tooltip,
      custom: ({ dataPointIndex, w }) => {
        const hold = w.globals.initialSeries?.[0]?.data?.[dataPointIndex] ?? 0;
        const charged =
          w.globals.initialSeries?.[1]?.data?.[dataPointIndex] ?? 0;
        const spent = charged + hold;

        const label = w.globals.labels?.[dataPointIndex] ?? '';

        return `
          <div class="spent-tooltip">
            <p><span>${label}</span><span>${formatBalanceUi(spent, currency)}</span></p>
            <p><span>${i18n.t('charged')}:</span><span>${formatBalanceUi(charged, currency)}</span></p>
            <p><span>${i18n.t('hold')}:</span><span>${formatBalanceUi(hold, currency)}</span></p>
          </div>
        `;
      },
    },
  };
}

//-------

//------- decline rate chart
function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay() || 7; // Mon=1 ... Sun=7
  d.setDate(d.getDate() - day + 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function getCategories(period, now = new Date()) {
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  const locale = getLocale();

  switch (period) {
    case 'month': {
      const y = end.getFullYear();
      const m = end.getMonth();
      const days = new Date(y, m + 1, 0).getDate();
      const monthName = end.toLocaleString(locale, { month: 'short' });
      return Array.from({ length: days }, (_, i) => `${monthName} ${i + 1}`);
    }

    case 'week': {
      // Генерируем дни недели с использованием локализации
      const base = startOfWeek(end);
      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(base);
        d.setDate(d.getDate() + i);
        return d.toLocaleString(locale, { weekday: 'short' });
      });
    }

    default:
      return [];
  }
}

const declineGradientStops = (opacity) => [
  { offset: 0, color: '#e75e3c', opacity: opacity[0] },
  { offset: 55, color: '#e5a400', opacity: opacity[1] },
  { offset: 100, color: '#74d400', opacity: opacity[2] },
];

export function mapDeclinePeriodToSeries(periodBlock, period) {
  const keys = Object.keys(periodBlock ?? {}).filter((k) =>
    /^\d{4}-\d{2}-\d{2}$/.test(k),
  );

  return {
    labels: keys.map((k) => formatPeriodLabel(period, k)),
    rates: keys.map((k) => Number(periodBlock[k]?.rate) || 0),
  };
}

export function getDeclineChartOptions(categories = [], threshold) {
  return {
    chart: {
      type: 'area',
      ...chartBase,
    },
    stroke: {
      curve: 'smooth',
      width: [0, 3],
    },
    fill: {
      type: 'gradient',
      gradient: {
        type: 'vertical',
        shadeIntensity: 0,
        opacityFrom: 1,
        opacityTo: 1,
        colorStops: [
          declineGradientStops([0.45, 0.25, 0.05]),
          declineGradientStops([1, 1, 1]),
        ],
      },
    },
    markers: {
      size: 0,
      colors: ['#fff'],
      strokeColors: '#e5a400',
      strokeWidth: 2,
      hover: { size: 6 },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: {
      ...gridBase,
      xaxis: { lines: { show: false } },
    },
    xaxis: {
      categories,
      tooltip: { enabled: false },
      crosshairs: {
        stroke: { color: '#c5c5c5', width: 1, dashArray: 0 },
      },
      labels: {
        style: { ...axisLabelStyle, cssClass: 'apexcharts-xaxis-label' },
      },
    },
    yaxis: {
      min: 0,
      max: (max) => Math.max(threshold, Math.ceil(max / 10) * 10),
      tickAmount: 5,
      labels: {
        formatter: (val) => (val ? `${Math.round(val)}%` : ''),
        style: { ...axisLabelStyle, cssClass: 'apexcharts-yaxis-label' },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      followCursor: false,
      custom: ({ dataPointIndex, w }) => {
        const label = categories[dataPointIndex] ?? '';
        const rate = w.globals.initialSeries?.[1]?.data?.[dataPointIndex] ?? 0;

        return `
          <div class="spent-tooltip">
            <p><span>${label}</span><span>${rate}%</span></p>
          </div>
        `;
      },
    },
  };
}
//-------
